import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function RatingModal({ winery, onSubmit, onSkip, isSubmitting }) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');

  const handleSubmit = () => {
    if (rating > 0) {
      onSubmit(rating, comment.trim());
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center"
      onClick={onSkip}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-t-3xl p-6 pb-safe relative"
      >
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-stone-100 transition-colors"
        >
          <X className="w-5 h-5 text-stone-400" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-stone-800 mb-1">How was your visit?</h2>
          <p className="text-stone-500 text-sm">{winery?.name}</p>
        </div>

        <div className="flex justify-center gap-2 mb-6">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              onTouchStart={() => setRating(star)}
              className="p-1 transition-transform hover:scale-110 active:scale-95"
            >
              <Star
                className={`w-10 h-10 transition-colors ${
                  star <= displayRating
                    ? 'fill-[#C9A227] text-[#C9A227]'
                    : 'text-stone-300'
                }`}
              />
            </button>
          ))}
        </div>

        <p className="text-center text-sm text-stone-500 mb-4">
          {rating === 0 && 'Tap to rate'}
          {rating === 1 && 'Poor'}
          {rating === 2 && 'Fair'}
          {rating === 3 && 'Good'}
          {rating === 4 && 'Very Good'}
          {rating === 5 && 'Excellent!'}
        </p>

        <div className="mb-6">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience... (optional)"
            rows={3}
            className="w-full px-4 py-3 border border-stone-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#C9A227]/30 focus:border-[#722F37] text-stone-700 placeholder:text-stone-400"
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={rating === 0 || isSubmitting}
          className="w-full h-14 bg-[#722F37] hover:bg-[#5a252c] text-white text-lg font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed ring-2 ring-[#C9A227]/30"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Rating'}
        </Button>

        <button
          onClick={onSkip}
          disabled={isSubmitting}
          className="w-full mt-3 py-2 text-stone-500 hover:text-stone-700 text-sm transition-colors disabled:opacity-50"
        >
          Skip for now
        </button>
      </motion.div>
    </motion.div>
  );
}
